import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { AlertCircle, CheckCircle2, Clock, Calendar, Plus, ExternalLink } from 'lucide-react';
import moment from 'moment';

const Todos = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, overdue, completed
  const navigate = useNavigate();

  useEffect(() => {
    fetchTodos();
  }, [filter]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      let endpoint = '/todos';
      if (filter === 'overdue') {
        endpoint = '/todos/overdue';
      } else if (filter === 'pending') {
        // Fetch all todos and filter for Pending and In Progress on client side
        const response = await api.get('/todos');
        const allTodos = response.data.data || [];
        const pendingAndInProgress = allTodos.filter(t => 
          t.status === 'Pending' || t.status === 'In Progress'
        );
        setTodos(pendingAndInProgress);
        setLoading(false);
        return;
      } else if (filter === 'completed') {
        const response = await api.get('/todos', { params: { status: 'Completed' } });
        setTodos(response.data.data);
        setLoading(false);
        return;
      }
      const response = await api.get(endpoint);
      setTodos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching todos:', error);
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Completed' || status === 'Cancelled') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const getDaysOverdue = (dueDate) => {
    const days = moment().diff(moment(dueDate), 'days');
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    if (days < 365) {
      const months = Math.floor(days / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    const years = Math.floor(days / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Urgent: 'bg-red-100 text-red-800 border-red-200',
      High: 'bg-orange-100 text-orange-800 border-orange-200',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Low: 'bg-green-100 text-green-800 border-green-200',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: 'bg-purple-100 text-purple-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      Completed: 'bg-green-100 text-green-800',
      Cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const updateTodoStatus = async (todoId, newStatus) => {
    try {
      await api.put(`/todos/${todoId}`, { status: newStatus });
      fetchTodos();
    } catch (error) {
      console.error('Error updating todo:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pendingTodos = todos.filter(t => t.status === 'Pending' || t.status === 'In Progress');
  const overdueTodos = pendingTodos.filter(t => isOverdue(t.dueDate, t.status));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">To-Do's</h1>
          <p className="text-gray-600 mt-1">Manage your tasks with deadline alerts</p>
        </div>
        <button
          onClick={() => navigate('/todos/create')}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>New To-Do</span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              filter === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            All ({todos.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              filter === 'pending'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Pending/In Progress ({pendingTodos.length})
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              filter === 'overdue'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Overdue ({overdueTodos.length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              filter === 'completed'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Completed ({todos.filter(t => t.status === 'Completed').length})
          </button>
        </div>
      </div>

      {/* My Pending To-Dos Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">My Pending To-Dos (with deadline alerts)</h2>
            <Link
              to="/todos?filter=all"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View All
            </Link>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {pendingTodos.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No pending to-dos</p>
            </div>
          ) : (
            pendingTodos.map((todo) => {
              const overdue = isOverdue(todo.dueDate, todo.status);
              const daysOverdue = overdue ? getDaysOverdue(todo.dueDate) : null;
              const daysUntilDue = !overdue ? moment(todo.dueDate).diff(moment(), 'days') : null;

              return (
                <div
                  key={todo.id || todo._id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    overdue ? 'bg-red-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    {/* Red Exclamation Mark for Overdue */}
                    {overdue && (
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                          <AlertCircle className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          {todo.caseId && (
                            <span className="text-sm font-medium text-gray-600">
                              {todo.caseId.caseNumber || 'Case'}
                            </span>
                          )}
                          {todo.flagId && (
                            <span className="text-sm font-medium text-gray-600">
                              {todo.flagId.flagNumber || 'Flag'}
                            </span>
                          )}
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              todo.status
                            )}`}
                          >
                            {todo.status}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                              todo.priority
                            )}`}
                          >
                            {todo.priority}
                          </span>
                          {overdue && (
                            <span className="text-sm text-gray-600 font-medium">
                              Due {daysOverdue}
                            </span>
                          )}
                          {!overdue && daysUntilDue !== null && (
                            <span className="text-sm text-gray-600">
                              {daysUntilDue === 0
                                ? 'Due today'
                                : daysUntilDue === 1
                                ? 'Due tomorrow'
                                : `Due in ${daysUntilDue} days`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {todo.caseId && (
                            <Link
                              to={`/cases/${todo.caseId.id || todo.caseId._id}`}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          )}
                          <button
                            onClick={() =>
                              updateTodoStatus(
                                todo.id || todo._id,
                                todo.status === 'Completed' ? 'Pending' : 'Completed'
                              )
                            }
                            className={`p-1 rounded ${
                              todo.status === 'Completed'
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <Link
                        to={`/todos/${todo.id || todo._id}`}
                        className="text-base font-medium text-gray-900 mb-2 hover:text-blue-600 cursor-pointer block"
                      >
                        {todo.title}
                      </Link>

                      {todo.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {todo.description}
                        </p>
                      )}

                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>
                            Created: {moment(todo.createdAt).format('DD-MMM-YYYY hh:mm A')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            Updated: {moment(todo.updatedAt).format('DD-MMM-YYYY hh:mm A')}
                          </span>
                        </div>
                        {todo.assignedTo && (
                          <div className="flex items-center space-x-1">
                            <span>
                              Assigned to: {todo.assignedTo.firstName} {todo.assignedTo.lastName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Completed To-Dos (if viewing all) */}
      {filter === 'all' && todos.filter(t => t.status === 'Completed').length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Completed To-Dos</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {todos
              .filter(t => t.status === 'Completed')
              .map((todo) => (
                <div key={todo.id || todo._id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-medium text-gray-900 line-through">
                          {todo.title}
                        </h3>
                        <span className="text-xs text-gray-500">
                          Completed: {moment(todo.completedDate).format('DD-MMM-YYYY')}
                        </span>
                      </div>
                      {todo.description && (
                        <p className="text-sm text-gray-500 line-clamp-2">{todo.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Todos;
